// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Badge, Button, Group, Radio, Select, SimpleGrid, Stack, Text, ThemeIcon } from '@mantine/core';
import type { JSX, ReactNode } from 'react';
import { useState } from 'react';
import { Navigate } from 'react-router';
import type { PaymentChoice } from './answers';
import { useIntakeV3Answers } from './answers';
import { ageFromDateOfBirth, MINIMUM_AGE } from './eligibility';
import { Note } from './IntakeV3Controls';
import { IntakeV3Frame } from './IntakeV3Frame';
import { AlertIcon, ArrowIcon, CardIcon, ShieldIcon, WalletIcon } from './icons';
import type { PlanChoice } from './plans';
import { getPlanOption, OTHER_PLAN, PLAN_OPTIONS } from './plans';
import { Question } from './Question';
import type { SelfPayOption } from './self-pay';
import { BUNDLE_SAVINGS, formatDollars, SELF_PAY_PRICES } from './self-pay';
import type { IntakeV3StepProps } from './steps';
import { getIntakeV3StepPathBySlug } from './steps';

const PAYMENT_OPTIONS: { value: PaymentChoice; title: string; description: string; icon: ReactNode }[] = [
  {
    value: 'insurance',
    title: 'I have insurance',
    description: 'Member ID and card on the next step.',
    icon: <CardIcon />,
  },
  {
    value: 'self-pay',
    title: "I'll pay myself",
    description: 'Flat visit price. No surprise bills.',
    icon: <WalletIcon />,
  },
];

const SELF_PAY_OPTIONS: { value: SelfPayOption; title: string; price: number; pill?: string; description: string }[] = [
  {
    value: 'bundle',
    title: 'Get Answers bundle',
    price: SELF_PAY_PRICES.bundle,
    pill: `Save ${formatDollars(BUNDLE_SAVINGS)}`,
    description:
      'First visit, home sleep test and results visit. Charged when you sign after your first visit, before your home sleep test is ordered.',
  },
  {
    value: 'per-visit',
    title: 'Pay per visit',
    price: SELF_PAY_PRICES.firstVisit,
    description: `First visit ${formatDollars(SELF_PAY_PRICES.firstVisit)}, home sleep test ${formatDollars(
      SELF_PAY_PRICES.sleepTest
    )}, results visit ${formatDollars(SELF_PAY_PRICES.resultsVisit)}. Each is charged when it happens.`,
  },
];

const COVERED_PLANS_SENTENCE = PLAN_OPTIONS.filter((option) => option.value !== OTHER_PLAN)
  .map((option) => option.label)
  .join(', ')
  .replace(/, ([^,]+)$/, ', and $1');

/**
 * How the patient pays: an insurance plan, or one of the two self-pay options.
 * Insured patients go on to their plan details; self-pay patients skip them.
 */
export function PaymentStep({ step, onBack, onContinue }: IntakeV3StepProps): JSX.Element {
  const { answers, updateAnswers } = useIntakeV3Answers();
  const { payment, selfPayOption } = answers.eligibility;
  const [attempted, setAttempted] = useState(false);

  const age = ageFromDateOfBirth(answers.eligibility.dateOfBirth);
  if (age === null || age < MINIMUM_AGE) {
    return <Navigate to={getIntakeV3StepPathBySlug('date-of-birth')} replace />;
  }

  const plan = getPlanOption(answers.coverage.plan);
  const isOtherPlan = plan?.value === OTHER_PLAN;

  const handleContinue = (): void => {
    setAttempted(true);
    if (payment === 'insurance' ? plan && !isOtherPlan : payment === 'self-pay' && selfPayOption) {
      onContinue();
    }
  };

  return (
    <IntakeV3Frame
      currentSection={step.section}
      title={step.title}
      description="Nothing is charged today."
      onBack={onBack}
      actions={
        <Button rightSection={<ArrowIcon />} onClick={handleContinue}>
          Continue
        </Button>
      }
    >
      <Stack gap={20}>
        <Radio.Group
          aria-label={step.title}
          value={payment ?? null}
          onChange={(value: string) => updateAnswers('eligibility', { payment: value as PaymentChoice })}
          error={attempted && !payment ? "Choose how you'll pay" : undefined}
        >
          <SimpleGrid cols={{ base: 1, xs: 2 }} spacing={10}>
            {PAYMENT_OPTIONS.map((option) => (
              <Radio.Card key={option.value} value={option.value} aria-label={option.title}>
                <Group wrap="nowrap" align="flex-start" gap={13}>
                  <Radio.Indicator />
                  <ThemeIcon variant="light">{option.icon}</ThemeIcon>
                  <Stack gap={5}>
                    <Text fw={700} fz={15} lh={1.35} c="var(--ink-strong)">
                      {option.title}
                    </Text>
                    <Text fz={13} lh={1.55} c="var(--muted)">
                      {option.description}
                    </Text>
                  </Stack>
                </Group>
              </Radio.Card>
            ))}
          </SimpleGrid>
        </Radio.Group>

        {payment === 'insurance' && (
          <Stack gap={12}>
            <Select
              label="Insurance plan name"
              placeholder="Search or choose your plan"
              data={PLAN_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
              searchable
              value={plan?.value ?? null}
              onChange={(value: string | null) =>
                updateAnswers('coverage', {
                  plan: (value ?? undefined) as PlanChoice | undefined,
                  planPayer: undefined,
                  payer: undefined,
                })
              }
              error={attempted && !plan ? 'Choose your plan' : undefined}
            />
            {plan && !isOtherPlan && (
              <Note icon={<ShieldIcon />}>
                Polaris works with {plan.label}. We'll confirm your coverage before your visit.
              </Note>
            )}
            {/* Stopgap until SLE-935's "We don't take that plan yet" screen. */}
            {isOtherPlan && (
              <Note tone="orange" icon={<AlertIcon />}>
                Polaris works with {COVERED_PLANS_SENTENCE}. We don't take other plans yet. Choose "I'll pay myself" to
                book as self-pay.
              </Note>
            )}
          </Stack>
        )}

        {payment === 'self-pay' && (
          <Question
            label="Choose how to pay"
            variant="section"
            error={attempted && !selfPayOption ? 'Choose a self-pay option' : null}
          >
            <Radio.Group
              aria-label="Self-pay options"
              value={selfPayOption ?? null}
              onChange={(value: string) => updateAnswers('eligibility', { selfPayOption: value as SelfPayOption })}
            >
              <Stack gap={10}>
                {SELF_PAY_OPTIONS.map((option) => (
                  <Radio.Card key={option.value} value={option.value} aria-label={option.title}>
                    <Group wrap="nowrap" align="flex-start" gap={13}>
                      <Radio.Indicator />
                      <Stack gap={5} style={{ flex: 1 }}>
                        <Group justify="space-between" wrap="nowrap" gap={8}>
                          <Group gap={8}>
                            <Text fw={700} fz={15} lh={1.35} c="var(--ink-strong)">
                              {option.title}
                            </Text>
                            {option.pill && (
                              <Badge size="sm" variant="light" color="green">
                                {option.pill}
                              </Badge>
                            )}
                          </Group>
                          <Text fw={700} fz={15} lh={1.35} c="var(--ink-strong)">
                            {formatDollars(option.price)}
                          </Text>
                        </Group>
                        <Text fz={13} lh={1.55} c="var(--muted)">
                          {option.description}
                        </Text>
                      </Stack>
                    </Group>
                  </Radio.Card>
                ))}
              </Stack>
            </Radio.Group>
          </Question>
        )}
      </Stack>
    </IntakeV3Frame>
  );
}
